/**
 * Strict decoder for the proxy's type-tagged GraphDelta documents — the
 * payload a commit stores and the detail endpoint returns. The encoding's
 * no-bare-numbers invariant is what makes this safe in JavaScript at all:
 * `JSON.parse` never sees a numeric token in a value position, so nothing is
 * mangled through `number`; every numeric arrives as a tagged string and is
 * decoded deliberately.
 *
 * Decoding keeps display fidelity without ever passing through `number`:
 * ints stay exact strings (render verbatim; `intToBigInt` on demand), floats
 * keep their source string, temporals/points/bytes keep tag + payload for
 * typed rendering. Same strictness as the Go decoder — an unrecognized
 * construct is an error naming the path, never a guess: the payload is
 * canonical history.
 */

import type { TaggedValue } from './types'

/** The decoder's envelope version; documents with any other `v` are rejected. */
export const DELTA_FORMAT_VERSION = 1

export type DecodedValue =
  | null
  | string
  | boolean
  | DecodedValue[]
  | { kind: 'int'; text: string }
  | { kind: 'float'; text: string }
  | { kind: 'bytes'; base64: string }
  | { kind: 'date'; text: string }
  | { kind: 'localtime'; text: string }
  | { kind: 'offsettime'; text: string }
  | { kind: 'localdatetime'; text: string }
  | { kind: 'zoneddatetime'; text: string; tz?: string }
  | { kind: 'duration'; months: string; days: string; seconds: string; nanos: string }
  | { kind: 'point'; srid: string; x: string; y: string; z?: string }

export interface DecodedNode {
  id: string
  labels: string[]
  props: Record<string, DecodedValue>
}

export interface DecodedRel {
  id: string
  type: string
  startId: string
  endId: string
  props: Record<string, DecodedValue>
}

/** The decoded delta, mirroring the wire envelope's grouping. */
export interface DecodedDelta {
  nodesAdded: DecodedNode[]
  nodesChanged: DecodedNode[]
  nodesRemoved: string[]
  relsAdded: DecodedRel[]
  relsChanged: DecodedRel[]
  relsRemoved: string[]
}

/** Thrown on any unrecognized or lossy construct, naming the offending path. */
export class DeltaDecodeError extends Error {
  readonly path: string

  constructor(path: string, message: string) {
    super(`delta decode: ${path}: ${message}`)
    this.name = 'DeltaDecodeError'
    this.path = path
  }
}

/** Converts a decoded int to BigInt for future numeric consumers. */
export function intToBigInt(v: { kind: 'int'; text: string }): bigint {
  return BigInt(v.text)
}

/**
 * Structural equality over decoded values — the UI-side mirror of the merge
 * engine's codec-canonical equality: kind plus exact payload, so a numeric
 * type flip (int 5 vs float 5) reads as different even though both render
 * "5", and NaN equals NaN (both carry the payload text "NaN"). Use this —
 * never rendered-text comparison — wherever the UI decides whether two
 * property values differ.
 */
export function decodedValueEqual(a: DecodedValue, b: DecodedValue): boolean {
  if (a === b) return true
  if (a === null || b === null) return false
  if (typeof a !== 'object' || typeof b !== 'object') return false
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
    return a.every((el, i) => decodedValueEqual(el, b[i]))
  }
  if (a.kind !== b.kind) return false
  switch (a.kind) {
    case 'int':
    case 'float':
    case 'date':
    case 'localtime':
    case 'offsettime':
    case 'localdatetime':
      return a.text === (b as { text: string }).text
    case 'zoneddatetime': {
      const bb = b as { text: string; tz?: string }
      return a.text === bb.text && a.tz === bb.tz
    }
    case 'bytes':
      return a.base64 === (b as { base64: string }).base64
    case 'duration': {
      const bb = b as { months: string; days: string; seconds: string; nanos: string }
      return (
        a.months === bb.months &&
        a.days === bb.days &&
        a.seconds === bb.seconds &&
        a.nanos === bb.nanos
      )
    }
    case 'point': {
      const bb = b as { srid: string; x: string; y: string; z?: string }
      return a.srid === bb.srid && a.x === bb.x && a.y === bb.y && a.z === bb.z
    }
  }
}

const SCALAR_TAGS: Record<
  string,
  'int' | 'float' | 'bytes' | 'date' | 'localtime' | 'offsettime' | 'localdatetime'
> = {
  i: 'int',
  f: 'float',
  b: 'bytes',
  d: 'date',
  lt: 'localtime',
  ot: 'offsettime',
  ldt: 'localdatetime'
}

function decodeValue(raw: unknown, path: string): DecodedValue {
  if (raw === null) return null
  if (typeof raw === 'string' || typeof raw === 'boolean') return raw
  if (typeof raw === 'number') {
    // The codec never emits bare numbers; one means a foreign or lossy payload.
    throw new DeltaDecodeError(path, 'bare JSON number (lossy payload?)')
  }
  if (Array.isArray(raw)) {
    return raw.map((el, i) => decodeValue(el, `${path}[${i}]`))
  }
  if (typeof raw === 'object') {
    return decodeTagged(raw as Record<string, unknown>, path)
  }
  throw new DeltaDecodeError(path, `malformed value of type ${typeof raw}`)
}

function decodeTagged(obj: Record<string, unknown>, path: string): DecodedValue {
  const t = obj.t
  if (typeof t !== 'string') {
    throw new DeltaDecodeError(path, 'object without "t" tag')
  }

  const scalarKind = SCALAR_TAGS[t]
  if (scalarKind) {
    if (typeof obj.v !== 'string') {
      throw new DeltaDecodeError(path, `bad "${t}" payload: expected string`)
    }
    if (scalarKind === 'bytes') return { kind: 'bytes', base64: obj.v }
    return { kind: scalarKind, text: obj.v }
  }

  switch (t) {
    case 'zdt': {
      if (typeof obj.v !== 'string') {
        throw new DeltaDecodeError(path, 'bad "zdt" payload: expected string')
      }
      const out: { kind: 'zoneddatetime'; text: string; tz?: string } = {
        kind: 'zoneddatetime',
        text: obj.v
      }
      if (typeof obj.tz === 'string' && obj.tz.length > 0) out.tz = obj.tz
      return out
    }
    case 'dur': {
      const parts = obj.v
      if (!Array.isArray(parts) || parts.length !== 4 || parts.some((p) => typeof p !== 'string')) {
        throw new DeltaDecodeError(path, 'bad duration payload: expected 4 strings')
      }
      const [months, days, seconds, nanos] = parts as string[]
      return { kind: 'duration', months, days, seconds, nanos }
    }
    case 'p2':
    case 'p3': {
      const { srid, x, y, z } = obj
      if (typeof srid !== 'string' || typeof x !== 'string' || typeof y !== 'string') {
        throw new DeltaDecodeError(path, `bad "${t}" payload: expected string coordinates`)
      }
      if (t === 'p3' && typeof z !== 'string') {
        throw new DeltaDecodeError(path, 'bad "p3" payload: missing z')
      }
      const point: { kind: 'point'; srid: string; x: string; y: string; z?: string } = {
        kind: 'point',
        srid,
        x,
        y
      }
      if (t === 'p3') point.z = z as string
      return point
    }
    default:
      throw new DeltaDecodeError(path, `unknown tag "${t}"`)
  }
}

function decodeProps(raw: unknown, path: string): Record<string, DecodedValue> {
  if (raw === undefined || raw === null) return {}
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    throw new DeltaDecodeError(path, 'props must be an object')
  }
  const out: Record<string, DecodedValue> = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    out[key] = decodeValue(value, `${path}.${key}`)
  }
  return out
}

function decodeStringList(raw: unknown, path: string): string[] {
  if (raw === undefined || raw === null) return []
  if (!Array.isArray(raw) || raw.some((el) => typeof el !== 'string')) {
    throw new DeltaDecodeError(path, 'expected a list of ids')
  }
  return raw as string[]
}

function decodeNode(raw: unknown, path: string): DecodedNode {
  const obj = asObject(raw, path)
  return {
    id: requireString(obj.id, `${path}.id`),
    labels: decodeStringList(obj.labels, `${path}.labels`),
    props: decodeProps(obj.props, `${path}.props`)
  }
}

function decodeRel(raw: unknown, path: string): DecodedRel {
  const obj = asObject(raw, path)
  return {
    id: requireString(obj.id, `${path}.id`),
    type: requireString(obj.type, `${path}.type`),
    startId: requireString(obj.startId, `${path}.startId`),
    endId: requireString(obj.endId, `${path}.endId`),
    props: decodeProps(obj.props, `${path}.props`)
  }
}

function decodeList<T>(raw: unknown, path: string, one: (raw: unknown, path: string) => T): T[] {
  if (raw === undefined || raw === null) return []
  if (!Array.isArray(raw)) throw new DeltaDecodeError(path, 'expected a list')
  return raw.map((el, i) => one(el, `${path}[${i}]`))
}

function asObject(raw: unknown, path: string): Record<string, unknown> {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new DeltaDecodeError(path, 'expected an object')
  }
  return raw as Record<string, unknown>
}

function requireString(raw: unknown, path: string): string {
  if (typeof raw !== 'string') throw new DeltaDecodeError(path, 'expected a string')
  return raw
}

/**
 * Decodes one entity's tagged property map — the shape compare and merge
 * conflict payloads carry per node/rel. Throws `DeltaDecodeError` naming the
 * offending path.
 */
export function decodeTaggedProps(raw: unknown, path = 'properties'): Record<string, DecodedValue> {
  return decodeProps(raw, path)
}

// --- Encode path: an edited scalar back to its type-tagged wire value ---
//
// The content-level conflict editor lets an operator type a corrected value for
// a conflicting property; submitting it re-tags the entry into the same wire
// form entity props ship in. Scalars only — text / number / bool; structured
// values (temporals, points, bytes, lists) stay side-pick.

/** Scalar kinds the content-value editor supports. */
export type EditableScalarKind = 'string' | 'int' | 'float' | 'boolean'

/** Re-encoding either yields a tagged wire value or names why the input is invalid. */
export type EncodeScalarResult = { ok: true; value: TaggedValue } | { ok: false; error: string }

/**
 * The editable scalar kind of a decoded value, or null when it is not an
 * editable scalar — structured props (temporals, points, bytes) and lists are
 * resolved by side pick, not by editing.
 */
export function editableScalarKind(value: DecodedValue): EditableScalarKind | null {
  if (typeof value === 'string') return 'string'
  if (typeof value === 'boolean') return 'boolean'
  if (value !== null && !Array.isArray(value) && typeof value === 'object') {
    if (value.kind === 'int') return 'int'
    if (value.kind === 'float') return 'float'
  }
  return null
}

/** The editor's seed text for a decoded scalar (empty for a non-scalar). */
export function scalarEditText(value: DecodedValue): string {
  if (typeof value === 'string') return value
  if (typeof value === 'boolean') return String(value)
  if (
    value !== null &&
    !Array.isArray(value) &&
    typeof value === 'object' &&
    (value.kind === 'int' || value.kind === 'float')
  ) {
    return value.text
  }
  return ''
}

/**
 * Re-encodes an edited scalar to its type-tagged wire value, validating numeric
 * input so a malformed entry never reaches the merge engine. Strings pass
 * through verbatim (whitespace can be meaningful); an int keeps its exact
 * decimal string (no float64 round-trip); a float must parse to a finite
 * number.
 */
export function encodeEditedScalar(kind: EditableScalarKind, input: string): EncodeScalarResult {
  switch (kind) {
    case 'string':
      return { ok: true, value: input }
    case 'boolean': {
      const t = input.trim()
      if (t === 'true') return { ok: true, value: true }
      if (t === 'false') return { ok: true, value: false }
      return { ok: false, error: 'Enter true or false.' }
    }
    case 'int': {
      const t = input.trim()
      if (!/^[+-]?\d+$/.test(t)) return { ok: false, error: 'Enter a whole number.' }
      return { ok: true, value: { t: 'i', v: t } }
    }
    case 'float': {
      const t = input.trim()
      if (t === '' || !Number.isFinite(Number(t))) return { ok: false, error: 'Enter a number.' }
      return { ok: true, value: { t: 'f', v: t } }
    }
  }
}

/**
 * Decodes a stored delta document (the parsed JSON of `CommitDetail.delta`).
 * Throws `DeltaDecodeError` on an unknown envelope version or any
 * unrecognized value construct.
 */
export function decodeDelta(raw: unknown): DecodedDelta {
  const obj = asObject(raw, 'delta')
  if (obj.v !== DELTA_FORMAT_VERSION) {
    throw new DeltaDecodeError('delta.v', `unsupported delta version ${String(obj.v)}`)
  }
  return {
    nodesAdded: decodeList(obj.nodesAdded, 'delta.nodesAdded', decodeNode),
    nodesChanged: decodeList(obj.nodesChanged, 'delta.nodesChanged', decodeNode),
    nodesRemoved: decodeStringList(obj.nodesRemoved, 'delta.nodesRemoved'),
    relsAdded: decodeList(obj.relsAdded, 'delta.relsAdded', decodeRel),
    relsChanged: decodeList(obj.relsChanged, 'delta.relsChanged', decodeRel),
    relsRemoved: decodeStringList(obj.relsRemoved, 'delta.relsRemoved')
  }
}
